import { useNavigate, useLocation } from "react-router-dom";
import { showError } from "../utils/dialog";
import usePageMeta from "../hooks/usePageMeta";
import { useEffect, useState } from "react";
import { BASE_URL } from "../config";
import { formatAppIntlLabel, getAppDisplayTimezone } from "../utils/appDateTime";

export default function CheckInSuccess() {
  usePageMeta("Check In Success");
  const navigate = useNavigate();
  const location = useLocation();
  const { verificationMethod, account, capturedImage, similarity, accountId, firstName, lastName, isCheckout, noShiftFound } = location.state || {};
  
  // Build account object from state if not provided
  const accountData = account || (accountId ? {
    accountId,
    name: firstName && lastName ? `${firstName} ${lastName}` : `Account ${accountId}`,
    firstName,
    lastName
  } : null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [countdown, setCountdown] = useState(5);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          navigate("/checkin" + window.location.search); // Redirect to check-in page for next person
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearInterval(countdownTimer);
    };
  }, [navigate]);

  const formatTime = (date) =>
    formatAppIntlLabel(date, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: getAppDisplayTimezone(),
    });

  const getVerificationText = () => {
    if (noShiftFound) {
      return "Identity Verified";
    }
    // Check if this is a check-in or check-out from QR flow
    if (isCheckout) {
      return "Check Out Successful!";
    } else if (verificationMethod === "phone") {
      return "Phone Verification Successful!";
    } else if (verificationMethod === "face") {
      return "Check In Successful!";
    }
    return "Check In Successful!";
  };

  const getWelcomeText = () => {
    if (isCheckout) {
      if (accountData?.name) {
        return `Thank you, ${accountData.name}!`;
      }
      return "Thank you!";
    }
    if (accountData?.name) {
      return `Welcome, ${accountData.name}!`;
    }
    return "Welcome!";
  };

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Success Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-bold text-center mb-2 text-green-600">
          {getVerificationText()}
        </h1>

        <h2 className="text-lg font-semibold text-center mb-4 text-gray-800">
          {getWelcomeText()}
        </h2>

        {/* Current Time */}
        <div className="text-center mb-4">
          <div className="text-2xl font-bold text-gray-800">{formatTime(currentTime)}</div>
          <div className="text-sm text-gray-600">{currentTime.toDateString()}</div>
        </div>

        {/* Verification Details */}
        {account && (
          <div className="bg-green-50 p-3 rounded-lg mb-4">
            <div className="text-sm text-green-800">
              <div className="font-semibold">Account: {account.name}</div>
              <div>ID: {account.accountId}</div>
              {account.phone && <div>Phone: {account.phone}</div>}
              {verificationMethod && (
                <div>Method: {verificationMethod === "phone" ? "Phone OTP" : "Face Recognition"}</div>
              )}
              {similarity && (
                <div>Face Match: {similarity.toFixed(1)}%</div>
              )}
            </div>
          </div>
        )}

        {/* Photo if available */}
        {capturedImage && (
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-green-300">
              <img 
                src={capturedImage} 
                alt="Verified identity" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        {noShiftFound ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-center text-red-700 text-sm font-medium">
              Your identity was verified, but no scheduled shift was found for you right now. Please contact your supervisor if you believe this is an error.
            </p>
          </div>
        ) : (
          <p className="text-center text-gray-600 mb-4 text-sm">
            {isCheckout
              ? "You have been successfully checked out. Thank you!"
              : "You have been successfully checked in. Welcome!"
            }
          </p>
        )}

        {/* Auto-redirect notice */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600">
            Redirecting to check-in page in {countdown} seconds...
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Next person can use the system
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          <button
            onClick={async () => {
              try {
                // Check if this is a QR-based check-in or regular face recognition
                const qrValidation = localStorage.getItem('qrValidation');
                
                if (!qrValidation) {
                  // Regular face recognition (no QR) - just navigate to home
                  navigate("/checkin" + window.location.search, {
                    state: {
                      checkinComplete: true,
                      account: accountData
                    }
                  });
                  return;
                }

                // QR-based check-in flow
                const validationData = JSON.parse(qrValidation);
                const { mode, shiftId, checkinId } = validationData;

                // Get auth headers
                const token = localStorage.getItem("token");
                const headers = {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                };

                let response;
                if (mode === 'checkin') {
                  // Perform check-in
                  response = await fetch(`${BASE_URL}/api/shifts/qr-scan`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      token: localStorage.getItem('lastQrToken'), // Need to store the original token
                      method: 'face+otp',
                      action: 'perform' // Flag to actually perform the check-in/out
                    })
                  });
                } else {
                  // Perform check-out
                  response = await fetch(`${BASE_URL}/api/shifts/qr-scan`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      token: localStorage.getItem('lastQrToken'),
                      method: 'face+otp',
                      action: 'perform'
                    })
                  });
                }

                if (response.ok) {
                  const result = await response.json();
                  // Clear validation data
                  localStorage.removeItem('qrValidation');
                  localStorage.removeItem('lastQrToken');

                  // Navigate to home with success message
                  navigate("/checkin" + window.location.search, {
                    state: {
                      checkinComplete: true,
                      mode: result.mode,
                      shiftId: result.shiftId,
                      account: account
                    }
                  });
                } else {
                  const errorData = await response.json();
                  showError(errorData.message || "Check-in/out failed");
                }
              } catch (err) {
                console.error("Check-in/out error:", err);
                showError("Failed to complete check-in/out");
              }
            }}
            className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold text-base hover:bg-[#b91900] transition-colors"
          >
            {localStorage.getItem('qrValidation') ? 'COMPLETE CHECK IN/OUT' : 'CONTINUE'}
          </button>

          <button
            onClick={() => navigate("/checkin" + window.location.search)}
            className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold text-base hover:bg-gray-400 transition-colors"
          >
            BACK TO HOME
          </button>
        </div>
      </div>
    </div>
  );
}